import React from "react";
import { Button, Spinner } from "react-bootstrap";
import {
  FormControl as MUIFormControl,
  InputLabel,
  Select as MUISelect,
  MenuItem,
} from "@mui/material";
import { SelectChangeEvent } from "@mui/material/Select";
import { useNavigate } from "react-router-dom";
import { fetchAllTracksForCourses } from "../../../utils/trackUtils";
import authService from "../../../utils/authService";
import { FaChevronUp, FaChevronDown } from "react-icons/fa";
import ConfirmationModal from "../../../components/ConfirmationModal";
import { SUBTASK_TYPE } from "./ProjectConfigurationTypes";

interface Track {
  track_id: string;
  track_name: string;
}

interface Project {
  project_id: string;
  project_name: string;
  project_allowed_subjects?: string[];
  project_data?: any;
  status?: string;
  last_updatable_tag?: string;
}

interface Subject {
  subject_id: string;
  subject_name: string;
  is_project: boolean;
}

interface ContentTypeCounts {
  video: number;
  notes: number;
  mcq: number;
  coding: number;
  project_coding: number;
}

interface ContentTypeStatus {
  assigned: string[];
  uploaded: string[];
  approved: string[];
}

interface ContentStatusData {
  assigned: ContentTypeCounts;
  uploaded: ContentTypeCounts;
  approved: ContentTypeCounts;
  selected: ContentTypeCounts;
  total: ContentTypeCounts;
  video: ContentTypeStatus;
  notes: ContentTypeStatus;
  mcq: ContentTypeStatus;
  coding: ContentTypeStatus;
  project_coding: ContentTypeStatus;
}

interface Props {
  selectedTrack: string;
  selectedProject: string;
  selectedProjectName: string;
  loading: boolean;
  onTrackChange: (trackId: string) => void;
  onProjectChange: (
    projectId: string,
    projectName: string,
    projectAllowedSubjects?: string[],
    projectData?: any
  ) => void;
  onSubjectsChange: (subjects: Subject[]) => void;
  onAddPhase: () => void;
  contentStatusData?: ContentStatusData | null;
  onProjectStatusChange?: (status: string) => void;
  refreshProjectsRef?: React.MutableRefObject<(() => Promise<void>) | null>;
  onLastUpdatableTagChange?: (tag: string) => void;
}

const ProjectConfigurationHeader: React.FC<Props> = ({
  selectedTrack,
  selectedProject,
  selectedProjectName,
  loading,
  onTrackChange,
  onProjectChange,
  onSubjectsChange,
  onAddPhase,
  contentStatusData: propContentStatusData,
  onProjectStatusChange,
  refreshProjectsRef,
  onLastUpdatableTagChange,
}) => {
  const [tracks, setTracks] = React.useState<Track[]>([]);
  const [projects, setProjects] = React.useState<Project[]>([]);
  const [loadingTracks, setLoadingTracks] = React.useState(false);
  const [loadingProjects, setLoadingProjects] = React.useState(false);

  const navigate = useNavigate();
  const [openStats, setOpenStats] = React.useState(false);
  const [projectStatus, setProjectStatus] = React.useState<string>("");
  const [showConfigureModal, setShowConfigureModal] = React.useState(false);
  const [showPublishModal, setShowPublishModal] = React.useState(false);
  const [showSuccessModal, setShowSuccessModal] = React.useState(false);
  const [isUpdatingState, setIsUpdatingState] = React.useState(false);

  // ---------------- STATS JSON ----------------
  const [stats, setStats] = React.useState<{
    total: { video: number; notes: number; mcq: number; coding: number; project_coding: number };
    selected: { video: number; notes: number; mcq: number; coding: number; project_coding: number };
    assigned: { video: number; notes: number; mcq: number; coding: number; project_coding: number };
    uploaded: { video: number; notes: number; mcq: number; coding: number; project_coding: number };
    approved: { video: number; notes: number; mcq: number; coding: number; project_coding: number };
  } | null>(null);

  /**
   * Calculates the sum of all numeric values in an object
   * @param obj - Object with numeric values
   * @returns Sum of all values
   */
  const getSum = (obj: Record<string, number>) =>
    Object.values(obj).reduce((a, b) => a + b, 0);

  const gridStyle: React.CSSProperties = {
    display: "grid",
    gridTemplateColumns: "60px repeat(4, 80px) 100px",
    alignItems: "center",
  };

  // ---------------- EFFECTS ----------------
  /**
   * Effect: Fetches all tracks on component mount
   * Triggers: Once on mount (empty dependency array)
   */
  React.useEffect(() => {
    fetchTracks();
  }, []);

  /**
   * Effect: Handles track selection change
   * Step 1: If no track selected, clear projects, subjects, and stats
   * Step 2: If track selected, fetch projects and subjects for the track, clear stats
   * Triggers: When selectedTrack changes
   */
  React.useEffect(() => {
    if (!selectedTrack) {
      // Step 1: Clear related data
      setProjects([]);
      onSubjectsChange([]);
      setStats(null);
      return;
    }
    // Step 2: Fetch data for selected track
    fetchProjects(selectedTrack);
    fetchSubjectsForTrack(selectedTrack);
    setStats(null);
  }, [selectedTrack]);

  /**
   * Effect: Updates stats and project status when project or content status data changes
   * Step 1: If no project selected, clear stats
   * Step 2: If project selected, update stats from content status data and update project status
   * Triggers: When selectedProject, projects, or propContentStatusData changes
   */
  React.useEffect(() => {
    if (!selectedProject) {
      setStats(null);
      setProjectStatus("");
      return;
    }
    
    // Update stats from prop content status data
    if (propContentStatusData && (propContentStatusData.total || propContentStatusData.selected || propContentStatusData.assigned || propContentStatusData.uploaded || propContentStatusData.approved)) {
      setStats({
        total: propContentStatusData.total || { video: 0, notes: 0, mcq: 0, coding: 0, project_coding: 0 },
        selected: propContentStatusData.selected || { video: 0, notes: 0, mcq: 0, coding: 0, project_coding: 0 },
        assigned: propContentStatusData.assigned || { video: 0, notes: 0, mcq: 0, coding: 0, project_coding: 0 },
        uploaded: propContentStatusData.uploaded || { video: 0, notes: 0, mcq: 0, coding: 0, project_coding: 0 },
        approved: propContentStatusData.approved || { video: 0, notes: 0, mcq: 0, coding: 0, project_coding: 0 },
      });
    } else {
      setStats(null);
    }
    
    // Update project status from projects list
    const project = projects.find(p => p.project_id === selectedProject);
    if (project && project.status) {
      setProjectStatus(project.status);
      // Notify parent component of status change
      if (onProjectStatusChange) {
        onProjectStatusChange(project.status);
      }
    } else {
      setProjectStatus("");
      if (onProjectStatusChange) {
        onProjectStatusChange("");
      }
    }
  }, [selectedProject, projects, propContentStatusData, onProjectStatusChange]);

  /**
   * Effect: Triggers project change callback when projects list updates
   * Step 1: Check if projects list and selectedProject exist
   * Step 2: Find the selected project in the projects list
   * Step 3: Call onProjectChange with project details
   * Triggers: When projects array changes
   */
  React.useEffect(() => {
    // Step 1: Validation
    if (!projects.length) return;
    if (!selectedProject) return;
  
    // Step 2: Find project
    const project = projects.find(p => p.project_id === selectedProject);
    if (!project) return;
  
    // Step 3: Trigger callback - include assigned_cc_ids in projectData
    const projectDataWithCcIds = {
      ...project.project_data,
      assigned_cc_ids: (project as any).assigned_cc_ids
    };
    onProjectChange(
      project.project_id,
      project.project_name,
      project.project_allowed_subjects,
      projectDataWithCcIds
    );
    
    // Pass last_updatable_tag to parent
    if (onLastUpdatableTagChange && project.last_updatable_tag) {
      onLastUpdatableTagChange(project.last_updatable_tag);
    } else if (onLastUpdatableTagChange) {
      onLastUpdatableTagChange("");
    }
  }, [projects]);
  
  // ---------------- API CALLS ----------------
  /**
   * Fetches all available tracks for courses
   * Step 1: Set loading state to true
   * Step 2: Fetch tracks data
   * Step 3: Update tracks state
   * Step 4: Reset loading state
   */
  const fetchTracks = async () => {
    setLoadingTracks(true);
    try {
      const data = await fetchAllTracksForCourses();
      setTracks(data);
    } finally {
      setLoadingTracks(false);
    }
  };

  /**
   * Fetches all projects for a specific track
   * Step 1: Set loading state to true
   * Step 2: Fetch projects from API
   * Step 3: Update projects state
   * Step 4: Reset loading state
   * @param trackId - The track ID to fetch projects for
   */
  const fetchProjects = async (trackId: string) => {
    setLoadingProjects(true);
    try {
      const res = await authService.get(`admin/get-all-project/${trackId}`);
      setProjects(res.data.projects || []);
    } finally {
      setLoadingProjects(false);
    }
  };

  // Expose fetchProjects via ref
  React.useEffect(() => {
    if (refreshProjectsRef && selectedTrack) {
      refreshProjectsRef.current = async () => {
        await fetchProjects(selectedTrack);
      };
    }
    return () => {
      if (refreshProjectsRef) {
        refreshProjectsRef.current = null;
      }
    };
  }, [selectedTrack, refreshProjectsRef]);

  /**
   * Fetches subjects available for a specific track
   * Step 1: Fetch subjects from API
   * Step 2: Update subjects via callback
   * Step 3: On error, set empty array
   * @param trackId - The track ID to fetch subjects for
   */
  const fetchSubjectsForTrack = async (trackId: string) => {
    try {
      const res = await authService.get(
        `admin/get_subjects_for_track/${trackId}/`
      );
      onSubjectsChange(res.data.subjects || []);
    } catch {
      onSubjectsChange([]);
    }
  };


  // ---------------- HANDLERS ----------------
  /**
   * Handles track selection change event
   * @param e - The select change event
   */
  const handleTrackChange = (e: SelectChangeEvent<string>) => {
    onTrackChange(e.target.value);
  };

  /**
   * Handles project selection change event
   * Step 1: Extract project ID from event
   * Step 2: Find the selected project in projects array
   * Step 3: Update project status
   * Step 4: Call onProjectChange with project details
   * @param e - The select change event
   */
  const handleProjectChange = (e: SelectChangeEvent<string>) => {
    const projectId = e.target.value;
    const project = projects.find((p) => p.project_id === projectId);
    if (project && project.status) {
      setProjectStatus(project.status);
    } else {
      setProjectStatus("");
    }
    const projectDataWithCcIds = project?.project_data ? {
      ...project.project_data,
      assigned_cc_ids: (project as any).assigned_cc_ids
    } : undefined;
    onProjectChange(
      projectId,
      project?.project_name || "",
      project?.project_allowed_subjects,
      projectDataWithCcIds
    );
    
    // Pass last_updatable_tag to parent
    if (onLastUpdatableTagChange) {
      if (project?.last_updatable_tag) {
        onLastUpdatableTagChange(project.last_updatable_tag);
      } else {
        onLastUpdatableTagChange("");
      }
    }
  };

  /**
   * Checks if project can be published
   * Conditions:
   * - No content should be in assigned arrays for any type (video, notes, mcq, coding, project_coding)
   * - No content should be in uploaded arrays for any type (video, notes, mcq, coding, project_coding)
   * - At least one approved array should have length > 1
   */
  const canPublish = (): boolean => {
    if (!propContentStatusData) {
      return false;
    }

    // Check if all assigned arrays are empty (length === 0) for all types
    const allAssignedArraysEmpty = 
      (!propContentStatusData[SUBTASK_TYPE.VIDEO]?.assigned || propContentStatusData[SUBTASK_TYPE.VIDEO]!.assigned.length === 0) &&
      (!propContentStatusData[SUBTASK_TYPE.NOTES]?.assigned || propContentStatusData[SUBTASK_TYPE.NOTES]!.assigned.length === 0) &&
      (!propContentStatusData[SUBTASK_TYPE.MCQ]?.assigned || propContentStatusData[SUBTASK_TYPE.MCQ]!.assigned.length === 0) &&
      (!propContentStatusData[SUBTASK_TYPE.CODING]?.assigned || propContentStatusData[SUBTASK_TYPE.CODING]!.assigned.length === 0) &&
      (!propContentStatusData[SUBTASK_TYPE.PROJECT_CODING]?.assigned || propContentStatusData[SUBTASK_TYPE.PROJECT_CODING]!.assigned.length === 0);

    // Check if all uploaded arrays are empty (length === 0) for all types
    const allUploadedArraysEmpty = 
      (!propContentStatusData[SUBTASK_TYPE.VIDEO]?.uploaded || propContentStatusData[SUBTASK_TYPE.VIDEO]!.uploaded.length === 0) &&
      (!propContentStatusData[SUBTASK_TYPE.NOTES]?.uploaded || propContentStatusData[SUBTASK_TYPE.NOTES]!.uploaded.length === 0) &&
      (!propContentStatusData[SUBTASK_TYPE.MCQ]?.uploaded || propContentStatusData[SUBTASK_TYPE.MCQ]!.uploaded.length === 0) &&
      (!propContentStatusData[SUBTASK_TYPE.CODING]?.uploaded || propContentStatusData[SUBTASK_TYPE.CODING]!.uploaded.length === 0) &&
      (!propContentStatusData[SUBTASK_TYPE.PROJECT_CODING]?.uploaded || propContentStatusData[SUBTASK_TYPE.PROJECT_CODING]!.uploaded.length === 0);

    // All assigned and uploaded arrays must be empty
    if (!allAssignedArraysEmpty || !allUploadedArraysEmpty) {
      return false;
    }

    // Check if at least one approved type array has length > 0
    const hasApprovedContent = 
      (propContentStatusData[SUBTASK_TYPE.VIDEO]?.approved && propContentStatusData[SUBTASK_TYPE.VIDEO]!.approved.length > 0) ||
      (propContentStatusData[SUBTASK_TYPE.NOTES]?.approved && propContentStatusData[SUBTASK_TYPE.NOTES]!.approved.length > 0) ||
      (propContentStatusData[SUBTASK_TYPE.MCQ]?.approved && propContentStatusData[SUBTASK_TYPE.MCQ]!.approved.length > 0) ||
      (propContentStatusData[SUBTASK_TYPE.CODING]?.approved && propContentStatusData[SUBTASK_TYPE.CODING]!.approved.length > 0) ||
      (propContentStatusData[SUBTASK_TYPE.PROJECT_CODING]?.approved && propContentStatusData[SUBTASK_TYPE.PROJECT_CODING]!.approved.length > 0);

    return hasApprovedContent;
  };

  /**
   * Determines the button state and text based on project status and content status
   */
  const getButtonState = (): { text: string; disabled: boolean; onClick: () => void } => {
    if (!selectedProject || !projectStatus) {
      return { text: "", disabled: true, onClick: () => {} };
    }

    if (projectStatus === "WIP") {
      return {
        text: "Configure",
        disabled: loading || isUpdatingState,
        onClick: () => setShowConfigureModal(true)
      };
    }

    if (projectStatus === "Configured") {
      if (canPublish()) {
        return {
          text: "Publish",
          disabled: loading || isUpdatingState,
          onClick: () => setShowPublishModal(true)
        };
      } else {
        return {
          text: "Configured",
          disabled: true,
          onClick: () => {}
        };
      }
    }

    if (projectStatus === "Published") {
      return {
        text: "Published",
        disabled: true,
        onClick: () => {}
      };
    }

    return { text: "", disabled: true, onClick: () => {} };
  };

  /**
   * Updates project state to Configured
   */
  const handleConfigureConfirm = async () => {
    setShowConfigureModal(false);
    setIsUpdatingState(true);
    try {
      await authService.put('admin/project_state/update/', {
        project_id: parseInt(selectedProject),
        new_state: "Configured"
      });
      // Refresh projects to get updated status - useEffect will update projectStatus
      if (selectedTrack) {
        await fetchProjects(selectedTrack);
      }
      // Update project status to Configured
      setProjectStatus("Configured");
      if (onProjectStatusChange) {
        onProjectStatusChange("Configured");
      }
    } catch (error) {
      console.error('Error updating project state:', error);
    } finally {
      setIsUpdatingState(false);
    }
  };

  /**
   * Updates project state to Published
   */
  const handlePublishConfirm = async () => {
    setShowPublishModal(false);
    setIsUpdatingState(true);
    try {
      await authService.put('admin/project_state/update/', {
        project_id: parseInt(selectedProject),
        new_state: "Published"
      });
      // Refresh projects to get updated status - useEffect will update projectStatus
      if (selectedTrack) {
        await fetchProjects(selectedTrack);
      }
      // Show success modal after successful publish
      setShowSuccessModal(true);
    } catch (error) {
      console.error('Error updating project state:', error);
    } finally {
      setIsUpdatingState(false);
    }
  };

  /**
   * Handles navigation to project listing screen with track_id
   */
  const handleDone = () => {
    setShowSuccessModal(false);
    navigate('/admin/project', {
      state: {
        track_id: selectedTrack
      }
    });
  };

  // ---------------- RENDER ----------------
  return (
    <div
      style={{
        position: "sticky",
        top: 0,
        zIndex: 100,
        backgroundColor: "#fff",
        borderBottom: "1px solid #dee2e6",
        padding: "12px 16px",
        boxShadow: "0 2px 4px rgba(0,0,0,0.08)",
      }}
    >
      <div className="d-flex justify-content-between align-items-start gap-3">
        {/* LEFT CONTROLS */}
        <div className="d-flex align-items-center gap-3">
          <MUIFormControl size="small" style={{ minWidth: 180 }}>
            <InputLabel>Track</InputLabel>
            <MUISelect
              value={selectedTrack}
              label="Track"
              onChange={handleTrackChange}
              disabled={loadingTracks || loading}
            >
              <MenuItem value="">Select Track</MenuItem>
              {tracks.map((t) => (
                <MenuItem key={t.track_id} value={t.track_id}>
                  {t.track_name}
                </MenuItem>
              ))}
            </MUISelect>
          </MUIFormControl>

          <MUIFormControl size="small" style={{ minWidth: 180 }}>
            <InputLabel>Project</InputLabel>
            <MUISelect
              value={selectedProject}
              label="Project"
              onChange={handleProjectChange}
              disabled={loadingProjects || loading || !selectedTrack}
            >
              <MenuItem value="">Select Project</MenuItem>
              {projects.map((p) => (
                <MenuItem key={p.project_id} value={p.project_id}>
                  {p.project_name}
                </MenuItem>
              ))}
            </MUISelect>
          </MUIFormControl>
        </div>

        {/* STATS SECTION */}
        {stats && (
          <div>
            {/* HEADERS */}
            <div className="fw-semibold text-center" style={{ ...gridStyle}}>
              <span></span>
              <span>Total</span>
              <span>Selected</span>
              <span>Assigned</span>
              <span>Uploaded</span>
              <span>Approved 
                <span role="button" className="ms-2" onClick={() => setOpenStats(!openStats)}>
                {openStats ? <FaChevronUp size={12} /> : <FaChevronDown size={12} />}
              </span></span>
              
            </div>

            {/* SUMMARY */}
            <div
              style={{
                ...gridStyle,
                cursor: "pointer"
              }}
              className="text-center"
            >
              <span></span>
              <span>{getSum(stats.total)}</span>
              <span>{getSum(stats.selected)}</span>
              <span>{getSum(stats.assigned)}</span>
              <span>{getSum(stats.uploaded)}</span>
              <span>{getSum(stats.approved)}</span>
            </div>

            {/* DETAILS */}
            {openStats && (
              <div>
                {([
                  ["Videos", SUBTASK_TYPE.VIDEO],
                  ["Notes", SUBTASK_TYPE.NOTES],
                  ["MCQs", SUBTASK_TYPE.MCQ],
                  ["Coding", SUBTASK_TYPE.CODING],
                  ["Project Coding", SUBTASK_TYPE.PROJECT_CODING],
                ] as const).map(([label, key]) => (
                  <div
                    key={key}
                    style={{ ...gridStyle }}
                    className="text-center"
                  >
                    <span className="fw-semibold text-start">{label}</span>
                    <span>{stats.total[key as keyof typeof stats.total]}</span>
                    <span>{stats.selected[key as keyof typeof stats.selected]}</span>
                    <span>{stats.assigned[key as keyof typeof stats.assigned]}</span>
                    <span>{stats.uploaded[key as keyof typeof stats.uploaded]}</span>
                    <span>{stats.approved[key as keyof typeof stats.approved]}</span>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}

        {/* ACTION BUTTONS */}
        <div className="d-flex align-items-center gap-2">
          <Button variant="primary" onClick={onAddPhase} disabled={loading || isUpdatingState || !selectedProject}>
            + Phase
          </Button>
          {(() => {
            const buttonState = getButtonState();
            if (!buttonState.text) return null;
            return (
              <Button
                variant="warning"
                onClick={buttonState.onClick}
                disabled={buttonState.disabled}
              >
                {isUpdatingState ? (
                  <>
                    <Spinner animation="border" size="sm" className="me-2" />
                    {buttonState.text === "Configure" ? "Configuring..." : buttonState.text === "Publish" ? "Publishing..." : buttonState.text}
                  </>
                ) : (
                  buttonState.text
                )}
              </Button>
            );
          })()}
        </div>
      </div>

      {/* Configure Confirmation Modal */}
      <ConfirmationModal
        show={showConfigureModal}
        onHide={() => setShowConfigureModal(false)}
        onConfirm={handleConfigureConfirm}
        title="Confirm Configuration"
        message={`Are you sure you have completed the configuration state for project: ${selectedProjectName}`}
        confirmText="Yes"
        cancelText="No"
        variant="success"
        isLoading={isUpdatingState}
      />

      {/* Publish Confirmation Modal */}
      <ConfirmationModal
        show={showPublishModal}
        onHide={() => setShowPublishModal(false)}
        onConfirm={handlePublishConfirm}
        title="Confirm Publish"
        message={`Are you sure you want to Publish this project : ${selectedProjectName}`}
        confirmText="Yes"
        cancelText="No"
        variant="primary"
        isLoading={isUpdatingState}
      />

      {/* Success Modal */}
      <ConfirmationModal
        show={showSuccessModal}
        onHide={handleDone}
        onConfirm={handleDone}
        title="Success"
        message="Project successfully published"
        confirmText="Done"
        cancelText=""
        variant="success"
        isLoading={false}
      />
    </div>
  );
};

export default ProjectConfigurationHeader;

